import React, { useLayoutEffect, useRef, useState } from 'react';

import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import ReturnMark from '../../ReturnMark';

import { STAGES_RAIL_TEXTS } from './texts';
import type { StageChapter, StageChapterActor } from './types';
import { placeChapters } from './utils';

/** Who moved the claim, in its dot: Clive's green, a person's azure, and the secondary text's grey for the system. */
const ACTOR_DOT: Record<StageChapterActor, { border: string; fill: string }> = {
  agent: { border: 'border-green-600', fill: 'bg-green-600' },
  person: { border: 'border-azure-700', fill: 'bg-azure-700' },
  system: { border: 'border-slate-800', fill: 'bg-slate-800' },
};

interface StagesRailProps {
  /** The claim's stage changes, oldest first. */
  chapters: StageChapter[];
  /** How far down the log each chapter's entry is, from 0 to 1, by the chapter's index; `undefined` for one older. */
  offsets: (number | undefined)[];
  /** Whether the log draws its newest entry first, which puts its oldest end at the foot. */
  isReversed: boolean;
  /** The chapter the reader is in; -1 for none. */
  activeIndex: number;
  /** Whether the labels are drawn, which they are while the reader is on the rail. */
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  /** Takes the log to a chapter's entry. */
  onSelectChapter: (chapter: StageChapter) => void;
}

/**
 * The log's stages rail, a map of the log: a spine down the rail, and on it a dot for each of the claim's stage changes
 * at its entry's place down the log, so a dot's place on the spine is its entry's place in the scroll. A chapter older
 * than every entry the log has read in waits at the log's oldest end, in order, until its entries are read in. Two
 * dots closer than 16px are drawn 16px apart, the later one further down the spine.
 *
 * Folded, it is the spine and the dots alone. While the reader is on it, by pointer or by keyboard, each chapter's label
 * and detail hang beside its dot, over the log, and are part of the chapter's button: centred on the dot, though kept
 * within the rail and 16px clear of the next. Each chapter's row reaches from its label down to the next one and is as
 * wide as the widest label, so a pointer crossing from one label to the next stays on the rail; past the labels the
 * rail takes nothing, so a click there still reaches the log. Picking a chapter folds the rail. A step back wears the
 * stage bar's return mark, and its dot an amber halo, around the colour of who moved the claim.
 */
const StagesRail: React.FC<StagesRailProps> = ({
  chapters,
  offsets,
  isReversed,
  activeIndex,
  isExpanded,
  onExpandedChange,
  onSelectChapter,
}) => {
  const listRef = useRef<HTMLOListElement>(null);
  const [railHeight, setRailHeight] = useState(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const measure = () => setRailHeight(list.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);

    return () => observer.disconnect();
  }, []);

  // By the chapter's index: the log reads a new chapter's offset a render after the chapter arrives, before it paints.
  const places = placeChapters(
    chapters.map((_, index) => offsets[index]),
    railHeight,
    isReversed
  );

  return (
    <nav
      aria-label={STAGES_RAIL_TEXTS.LABEL}
      className="relative z-10 w-40 shrink-0"
      onMouseEnter={() => onExpandedChange(true)}
      onMouseLeave={() => onExpandedChange(false)}
      onFocus={() => onExpandedChange(true)}
      onBlur={(event) => {
        // Focus moving from one chapter to the next stays on the rail.
        if (!event.currentTarget.contains(event.relatedTarget)) onExpandedChange(false);
      }}
    >
      <span aria-hidden className="absolute inset-y-16 left-1/2 w-[1px] -translate-x-1/2 bg-slate-500" />
      {/* Unfolded, it reaches over the log as far as the labels do, so it leaves the pointer to the chapters' rows. The
          rows share the list's one grid cell, each pushed down to its place, so each is as wide as the widest label. */}
      <ol
        ref={listRef}
        className={cn(
          'absolute inset-y-0 right-0 m-0 grid list-none p-0',
          isExpanded ? 'pointer-events-none w-max' : 'w-40'
        )}
      >
        {chapters.map((chapter, index) => {
          const isActive = index === activeIndex;
          const dot = ACTOR_DOT[chapter.actor];
          const place = places[index];

          return (
            <li
              key={chapter.key}
              className={cn(
                'col-start-1 row-start-1 flex items-start justify-end self-start pr-12',
                isExpanded && 'pointer-events-auto'
              )}
              style={{ marginTop: place.rowTop, height: place.rowHeight }}
            >
              <button
                type="button"
                aria-current={isActive ? 'step' : undefined}
                onClick={() => {
                  onSelectChapter(chapter);
                  onExpandedChange(false);
                }}
                className="mr-24 flex h-40 cursor-pointer items-center border-0 bg-transparent p-0 text-right"
              >
                <span className={isExpanded ? 'whitespace-nowrap' : 'sr-only'}>
                  <Text variant={Text.VARIANTS.SM} weight={isActive ? Text.WEIGHTS.SEMI_BOLD : Text.WEIGHTS.REGULAR}>
                    {chapter.direction === 'back' ? <ReturnMark /> : null}
                    {chapter.label}
                  </Text>
                  <Text
                    variant={Text.VARIANTS.XS}
                    weight={Text.WEIGHTS.REGULAR}
                    colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                  >
                    {chapter.detail}
                  </Text>
                </span>
                {/* On the spine: 12px in from the list's right, the cell's middle is the rail's. */}
                <span
                  className="absolute right-12 grid h-16 w-16 -translate-y-1/2 place-items-center"
                  style={{ top: place.dot }}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'box-border rounded-full border-2 border-solid',
                      dot.border,
                      isActive ? cn('h-12 w-12', dot.fill) : 'h-8 w-8 bg-white',
                      chapter.direction === 'back' && 'ring-4 ring-yellow-700/50'
                    )}
                  />
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default StagesRail;
